import { Button } from "@games-monorepo/shared-ui";
import { useRef, useState } from "react";

interface FirstRunTutorialProps {
  onComplete: () => void;
  onBack: () => void;
  onStep: (step: number, action: string) => void;
}

export function FirstRunTutorial({ onComplete, onBack, onStep }: FirstRunTutorialProps) {
  const [step, setStep] = useState(0);
  const [steered, setSteered] = useState(false);
  const [direction, setDirection] = useState({ x: 0, y: -32 });
  const [loopClosed, setLoopClosed] = useState(false);
  const [tailCut, setTailCut] = useState(false);
  const pointerId = useRef<number | null>(null);

  const next = () => {
    if (step >= 2) {
      onStep(step, "completed");
      onComplete();
      return;
    }
    onStep(step, "advanced");
    setStep((current) => current + 1);
  };

  return (
    <div className="yak-trails__screen yak-trails__tutorial-screen">
      <header className="yak-trails__screen-header">
        <button type="button" className="yak-trails__icon-button" onClick={onBack} aria-label="Go back">←</button>
        <h2>Trail School</h2>
        <button type="button" className="yak-trails__tutorial-skip" onClick={onComplete}>Skip</button>
      </header>
      <div className="yak-trails__tutorial-progress" aria-label={`Tutorial step ${step + 1} of 3`}>
        {[0, 1, 2].map((value) => <span className={value <= step ? "is-active" : ""} key={value} />)}
      </div>

      {step === 0 && (
        <section className="yak-trails__tutorial-card">
          <span className="yak-trails__tutorial-kicker">STEP 1 • STEER</span>
          <h3>Drag anywhere</h3>
          <p>Your yak follows the direction of your finger. Let go and it keeps cruising.</p>
          <div
            className="yak-trails__steering-lab"
            role="application"
            aria-label="Drag to steer the tutorial yak"
            onPointerDown={(event) => {
              pointerId.current = event.pointerId;
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={(event) => {
              if (pointerId.current !== event.pointerId) return;
              const bounds = event.currentTarget.getBoundingClientRect();
              const x = event.clientX - (bounds.left + bounds.width / 2);
              const y = event.clientY - (bounds.top + bounds.height / 2);
              const magnitude = Math.hypot(x, y);
              if (magnitude < 12) return;
              setDirection({ x: x / magnitude * 36, y: y / magnitude * 36 });
              if (!steered) onStep(0, "steered");
              setSteered(true);
            }}
            onPointerUp={(event) => {
              if (pointerId.current === event.pointerId) pointerId.current = null;
            }}
            onPointerCancel={() => { pointerId.current = null; }}
          >
            <div className="yak-trails__tutorial-compass" />
            <div className="yak-trails__tutorial-trail" style={{ transform: `rotate(${Math.atan2(direction.y, direction.x)}rad)` }} />
            <div className="yak-trails__tutorial-yak" style={{ transform: `translate(${direction.x}px, ${direction.y}px) rotate(${Math.atan2(direction.y, direction.x) + Math.PI / 2}rad)` }}>◆</div>
            <small>{steered ? "Nice turn! Release to keep moving." : "Touch, then drag in any direction"}</small>
          </div>
          <Button disabled={!steered} onClick={next}>{steered ? "Next: claim territory" : "Try a drag first"}</Button>
        </section>
      )}

      {step === 1 && (
        <section className="yak-trails__tutorial-card">
          <span className="yak-trails__tutorial-kicker">STEP 2 • CLAIM</span>
          <h3>Leave. Loop. Return.</h3>
          <p>Leaving your color creates a vulnerable trail. Bring it back home to fill everything inside your loop.</p>
          <button
            type="button"
            className={`yak-trails__capture-lab ${loopClosed ? "is-complete" : ""}`}
            onClick={() => {
              setLoopClosed(true);
              onStep(1, "loop_closed");
            }}
            aria-label="Close the example capture loop"
          >
            <span className="yak-trails__capture-home" />
            <span className="yak-trails__capture-loop" />
            <span className="yak-trails__capture-runner">◆</span>
            <small>{loopClosed ? "Captured! The enclosed land is yours." : "Tap the yak to close its loop"}</small>
          </button>
          <Button disabled={!loopClosed} onClick={next}>{loopClosed ? "Next: cut a trail" : "Close the loop first"}</Button>
        </section>
      )}

      {step === 2 && (
        <section className="yak-trails__tutorial-card">
          <span className="yak-trails__tutorial-kicker">STEP 3 • SURVIVE</span>
          <h3>Cut theirs. Guard yours.</h3>
          <p>Touch an exposed enemy trail to eliminate its owner. Crossing your own exposed trail eliminates you.</p>
          <div className={`yak-trails__cut-lab ${tailCut ? "is-complete" : ""}`}>
            <span className="yak-trails__cut-enemy">■</span>
            <button type="button" onClick={() => {
              setTailCut(true);
              onStep(2, "tail_cut");
            }} aria-label="Cut the exposed pink trail" />
            <span className="yak-trails__cut-player">◆</span>
            <small>{tailCut ? "Perfect cut! Now protect your own trail." : "Tap the exposed pink trail"}</small>
          </div>
          <Button disabled={!tailCut} onClick={next}>{tailCut ? "I’m ready to play" : "Cut the trail first"}</Button>
        </section>
      )}
      <footer className="yak-trails__tutorial-footer">Heads can pass through each other • the trail is what matters</footer>
    </div>
  );
}
